import { useCallback, useEffect, useRef, useState } from "react";
import config from "../config/index.js";
import { fetchSystemActivity, fetchSystemStatus } from "../services/api.js";

/**
 * Unlisted live "System Activity" ops page. Shows, in plain English, exactly
 * what the protocol's automated treasury keeper is doing in real time — every
 * buy / sell (with on-chain tx hash + effect on supply & price), zone change,
 * panic trip and pause — polled every few seconds so nothing needs a reload.
 *
 * Self-contained (no app sidebar/nav) so a direct link can be shared with the
 * client. Not linked anywhere in the app.
 */
const POLL_MS = 5000;
const EXPLORER = config.network.blockExplorerUrl || "https://sepolia.basescan.org";

// Per-zone stance shown in the live header (colour + one-line summary).
const STANCE = {
    1: { label: "Defending the floor — BUYING $GILD", tone: "buy" },
    2: { label: "Holding — no trades (fair value)", tone: "idle" },
    3: { label: "Skimming the premium — SELLING slowly", tone: "sell" },
    4: { label: "Harvesting the premium — SELLING", tone: "sell" }
};

// Per-action visual treatment for the feed cards.
const ACTION = {
    sell: { tag: "SELL", glyph: "↓", tone: "sell" },
    buy: { tag: "BUY", glyph: "↑", tone: "buy" },
    zone_change: { tag: "ZONE", glyph: "⇄", tone: "zone" },
    panic_on: { tag: "PANIC", glyph: "⚠", tone: "alert" },
    panic_off: { tag: "RESUMED", glyph: "✓", tone: "buy" },
    pause: { tag: "PAUSED", glyph: "⏸", tone: "idle" },
    observe: { tag: "ORACLE", glyph: "◎", tone: "zone" },
    deposit: { tag: "DEPOSIT", glyph: "＋", tone: "buy" },
    reinvest: { tag: "REINVEST", glyph: "↻", tone: "buy" },
    loan: { tag: "LOAN", glyph: "◆", tone: "zone" },
    liquidation: { tag: "LIQUIDATION", glyph: "⚠", tone: "alert" },
    tvg_snapshot: { tag: "TVG", glyph: "◎", tone: "sell" }
};

const TONE = {
    buy: { dot: "#22C55E", text: "#22C55E", chip: "rgba(34,197,94,0.14)" },
    sell: { dot: "#FBC206", text: "#FBC206", chip: "rgba(251,194,6,0.14)" },
    idle: { dot: "#9aa79b", text: "#c9d4ca", chip: "rgba(154,167,155,0.14)" },
    zone: { dot: "#7ea8ff", text: "#a9c4ff", chip: "rgba(126,168,255,0.14)" },
    alert: { dot: "#ef5350", text: "#ff8a80", chip: "rgba(239,83,80,0.16)" }
};

function relTime(iso) {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const s = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
}

function shortHash(h) {
    return h ? `${h.slice(0, 8)}…${h.slice(-6)}` : "";
}

export default function SystemActivity() {
    const [status, setStatus] = useState(null);
    const [events, setEvents] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [now, setNow] = useState(Date.now());
    const timer = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const [st, act] = await Promise.allSettled([fetchSystemStatus(), fetchSystemActivity({ limit: 150 })]);
            if (st.status === "fulfilled") setStatus(st.value);
            if (act.status === "fulfilled") setEvents(act.value?.items || []);
        } catch {
            /* transient — keep last view */
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        refresh();
        timer.current = setInterval(refresh, POLL_MS);
        return () => clearInterval(timer.current);
    }, [refresh]);

    // 1s ticker so relative timestamps stay live between polls.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    // Live stance with a countdown — the momentum / panic / hold windows tick
    // down every second (the `now` ticker) so the header always feels live even
    // when no trade is happening (e.g. during a long anti-dump pause).
    const minsLeft = (until) => Math.max(0, Math.ceil(((until || 0) - now) / 60000));
    let stance;
    if (!status?.ready) {
        stance = { label: "Calibrating…", tone: "idle" };
    } else if (status.panic) {
        stance = { label: `PANIC — sells halted (~${minsLeft(status.panicUntil)}m left)`, tone: "alert" };
    } else if ((status.zone === 3 || status.zone === 4) && status.momentumUntil > now) {
        stance = {
            label: `Sells paused ~${minsLeft(status.momentumUntil)}m — anti-dump guard`,
            tone: "sell"
        };
    } else if (status.zone === 1 && status.holdUntil > now) {
        stance = {
            label: `Buy hold-off ~${minsLeft(status.holdUntil)}m — letting the market front-run`,
            tone: "buy"
        };
    } else {
        stance = STANCE[status.zone] || { label: "Calibrating…", tone: "idle" };
    }
    const stanceTone = TONE[stance.tone];

    // Hide legacy momentum-pause rows (a transient status, now shown live in the
    // header) so the feed only carries real actions.
    const feed = events.filter((e) => e.action !== "pause");

    return (
        <div className="min-h-screen w-full bg-[#0a1f10] px-4 py-6 text-white sm:px-8 sm:py-8" data-now={now}>
            <div className="mx-auto max-w-[900px]">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <div className="font-manrope text-[11px] font-bold uppercase tracking-[0.2em] text-[#c4b050]">
                            GILDer · Treasury Keeper
                        </div>
                        <h1 className="mt-1 font-serif text-[26px] font-semibold text-[#e6c985]">System Activity</h1>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10281b] px-3 py-1 text-[11px] font-semibold text-[#22C55E] ring-1 ring-[#22C55E]/40">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-70" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
                        </span>
                        LIVE
                    </span>
                </div>

                <p className="mt-2 max-w-[640px] text-[12px] leading-snug text-[#e6c985]/70">
                    A live view of the protocol&apos;s automated treasury. It buys $GILD when the price is below its
                    treasury-backed floor (TVG) and sells when it is above — every action below is on-chain and
                    verifiable. Updates automatically every {POLL_MS / 1000}s; no reload needed.
                </p>

                {/* Live stance card */}
                <div
                    className="mt-4 rounded-[14px] bg-[#10281b] p-4 ring-1 ring-[#bfb67b]/60"
                    style={{ boxShadow: `inset 4px 0 0 ${stanceTone.dot}` }}
                >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#e6c985]/60">
                                Right now
                            </div>
                            <div className="mt-1 font-serif text-[20px] font-semibold" style={{ color: stanceTone.text }}>
                                {stance.label}
                            </div>
                        </div>
                        <div className="flex gap-5">
                            <Stat label="Spot price" value={status?.spotUsd || "—"} />
                            <Stat label="Spot ÷ TVG" value={status?.tvgRatio || "—"} />
                            <Stat label="Zone" value={status?.zone ? `Zone ${status.zone}` : "—"} />
                        </div>
                    </div>
                </div>

                {/* Feed */}
                <div className="mt-5 mb-1 flex items-baseline justify-between">
                    <h2 className="font-manrope text-[12px] font-bold uppercase tracking-[0.14em] text-[#c4b050]">
                        Recent activity
                    </h2>
                    <span className="text-[11px] text-[#e6c985]/50">{feed.length} events</span>
                </div>

                {!loaded ? (
                    <div className="py-16 text-center text-[13px] text-[#e6c985]/50">Loading live feed…</div>
                ) : feed.length === 0 ? (
                    <div className="rounded-[12px] bg-[#10281b] px-4 py-12 text-center text-[13px] text-[#e6c985]/60 ring-1 ring-[#bfb67b]/40">
                        No actions recorded yet. Trades, deposits, reinvestments and zone changes will appear here
                        the moment they happen — the live status above updates every {POLL_MS / 1000}s.
                    </div>
                ) : (
                    <ul className="space-y-2.5">
                        {feed.map((e) => (
                            <EventCard key={e._id || `${e.createdAt}-${e.action}`} e={e} />
                        ))}
                    </ul>
                )}

                <div className="mt-8 text-center text-[10px] text-[#e6c985]/35">
                    {config.network.name} · every trade below is verifiable on-chain
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="text-right">
            <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-[#e6c985]/50">{label}</div>
            <div className="mt-0.5 font-mono text-[13px] font-semibold text-white">{value}</div>
        </div>
    );
}

function EventCard({ e }) {
    const a = ACTION[e.action] || { tag: e.action, glyph: "•", tone: "zone" };
    const tone = TONE[a.tone] || TONE.zone;
    return (
        <li
            className="rounded-[12px] bg-[#10281b] p-3.5 ring-1 ring-[#bfb67b]/30"
            style={{ boxShadow: `inset 3px 0 0 ${tone.dot}` }}
        >
            <div className="flex items-start gap-3">
                <span
                    className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[14px] font-bold"
                    style={{ backgroundColor: tone.chip, color: tone.text }}
                >
                    {a.glyph}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span
                            className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                            style={{ backgroundColor: tone.chip, color: tone.text }}
                        >
                            {a.tag}
                        </span>
                        <span className="text-[13.5px] font-semibold text-white">{e.title}</span>
                        <span className="ml-auto text-[10.5px] text-[#e6c985]/45" title={e.createdAt}>
                            {relTime(e.createdAt)}
                        </span>
                    </div>

                    {e.reason ? (
                        <p className="mt-1 text-[11.5px] leading-snug text-[#e6c985]/80">{e.reason}</p>
                    ) : null}

                    {e.effect ? (
                        <div
                            className="mt-1.5 inline-block rounded-md px-2 py-1 text-[11px] font-semibold"
                            style={{ backgroundColor: tone.chip, color: tone.text }}
                        >
                            {e.effect}
                        </div>
                    ) : null}

                    {/* Meta row */}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-[#e6c985]/55">
                        {e.amountGil ? <Meta k="Amount" v={`${e.amountGil} $GILD`} /> : null}
                        {e.amountUsdc ? <Meta k="Value" v={e.amountUsdc} /> : null}
                        {e.spotUsd ? <Meta k="Spot" v={e.spotUsd} /> : null}
                        {e.tvgRatio ? <Meta k="Ratio" v={e.tvgRatio} /> : null}
                        {e.txHash ? (
                            <a
                                href={`${EXPLORER}/tx/${e.txHash}`}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-auto inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#FBC206] hover:bg-white/10"
                            >
                                {shortHash(e.txHash)} ↗
                            </a>
                        ) : null}
                    </div>
                </div>
            </div>
        </li>
    );
}

function Meta({ k, v }) {
    return (
        <span>
            <span className="text-[#e6c985]/40">{k}:</span>{" "}
            <span className="font-mono text-white/85">{v}</span>
        </span>
    );
}
